import React, { useEffect, useRef, useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form, Field } from 'formik';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBreadcrumbStore } from 'stores';
import { Row, Col, Modal, Alert } from 'react-bootstrap';
//icons
import { AiOutlineSearch } from 'react-icons/ai';
import { FaXmark } from 'react-icons/fa6';
import { BsBook, BsPerson, BsBuilding, BsHash } from 'react-icons/bs';
//custom-hook
import useAssignedLibrary from 'utils/customHooks/useAssignedLibrary';
// components
import { InputCustom } from 'components/input';
import { UserContext } from 'context/userContext';
import './SearchBarDesktop.scss';

const SearchOptionsModal = ({ 
  show, 
  onHide, 
  searchValue, 
  currentSearchType, 
  onSelectOption, 
  onSubmit,
  advancedSearchPath,
  onAdvancedSearchClick,
}) => {
  const { t } = useTranslation(['header']);
  const [showEmptySearchAlert, setShowEmptySearchAlert] = useState(false);

  const handleOptionClick = (value) => {
    if (!searchValue?.trim()) {
      setShowEmptySearchAlert(true);
      return;
    }
    setShowEmptySearchAlert(false);
    onSelectOption(value);
    onHide();
    setTimeout(() => {
      if (onSubmit) {
        onSubmit(value);
      }
    }, 150);
  };

  const handleHide = () => {
    setShowEmptySearchAlert(false);
    onHide();
  };

  const searchOptions = [
    {
      id: 'titulo',
      label: t('title'),
      description: t('searchByTitle'),
      icon: <BsBook size={32} />,
      value: 'titulo'
    },
    {
      id: 'autor',
      label: t('author'),
      description: t('searchByAuthor'),
      icon: <BsPerson size={32} />,
      value: 'autor'
    },
    {
      id: 'editorial',
      label: t('editorial'),
      description: t('searchByEditorial'),
      icon: <BsBuilding size={32} />,
      value: 'editorial'
    },
    {
      id: 'ean',
      label: 'ISBN',
      description: t('searchByISBN'),
      icon: <BsHash size={32} />,
      value: 'ean'
    }
  ];

  return (
    <Modal 
      show={show} 
      onHide={handleHide} 
      centered 
      className="search-options-modal"
      backdrop={true}
    >
      <Modal.Header className="search-modal-header">
        <Modal.Title className="search-modal-title">
          {t('whatDoYouWantToSearch')}
        </Modal.Title>
        <button 
          type="button" 
          className="search-modal-close" 
          onClick={handleHide}
          aria-label="Cerrar"
        >
          <FaXmark size={20} />
        </button>
      </Modal.Header>
      <Modal.Body className="search-modal-body">
        {showEmptySearchAlert && (
          <Alert variant="warning" className="mb-3">
            {t('fillSearchFieldToSearch')}
          </Alert>
        )}
        {searchValue && (
          <div className="search-modal-current">
            {t('searching')}: "{searchValue}"
          </div>
        )}
        <Row className="search-options-grid">
          {searchOptions.map((option) => (
            <Col 
              xs={6} 
              key={option.id}
              className="search-option-col"
            >
              <div
                className={`search-option-card ${
                  currentSearchType === option.value ? 'selected' : ''
                }`}
                onClick={() => handleOptionClick(option.value)}
              >
                <div className="search-option-icon">
                  {option.icon}
                </div>
                <div className="search-option-label">
                  {option.label}
                </div>
                <div className="search-option-description">
                  {option.description}
                </div>
              </div>
            </Col>
          ))}
        </Row>
        <div className="search-modal-advanced">
          <Link 
            to={advancedSearchPath} 
            className="search-advanced-link"
            onClick={() => {
              if (onAdvancedSearchClick) {
                onAdvancedSearchClick();
              }
              handleHide();
            }}
          >
            {t('advancedSearch')}
          </Link>
        </div>
      </Modal.Body>
    </Modal>
  );
};

SearchOptionsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  searchValue: PropTypes.string,
  currentSearchType: PropTypes.string,
  onSelectOption: PropTypes.func.isRequired,
  onSubmit: PropTypes.func,
  advancedSearchPath: PropTypes.string.isRequired,
  onAdvancedSearchClick: PropTypes.func,
};

const SearchBar = ({ children, isDesktop, initialValues, onSearch, onSubmitCustom }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const setBreadcrumbTitle = useBreadcrumbStore((state) => state.setBreadcrumbTitle);
  const { t } = useTranslation(['header']);
  const formikRef = useRef(null);
  const { searchData, setSearchData } = useContext(UserContext);
  const { libraryPath } = useAssignedLibrary();
  const [showModal, setShowModal] = useState(false);
  const prevPathnameRef = useRef(location.pathname);

  // Usar searchData del contexto para que al limpiar búsqueda desde la lista se vacíe también el input del navbar
  const formValues = useMemo(() => {
    return {
      value: searchData?.value ?? '',
      searchType: searchData?.searchType || 'titulo'
    };
  }, [searchData?.value, searchData?.searchType]);

  const executeSearch = (values) => {
    const searchValue = values?.value ? values?.value.trim() : '';
    if (!searchValue) {
      return;
    }
    
    const searchType = values?.searchType;
    setSearchData({ value: searchValue, searchType });
    setBreadcrumbTitle(t('busqueda', { ns: 'backoffice' }));
    
    const { searchType: _, value: __, titulo, autor, editorial, ean, products, ...otherUrlParams } = values;
    const queryParams = {
      ...otherUrlParams,
    };
    
    if (searchType) {
      queryParams[searchType] = searchValue;
    } else {
      queryParams.products = searchValue;
    }
    
    const URLqueryParametersManager = new URLSearchParams(queryParams);
    const advancedSearchQueryParameters = URLqueryParametersManager.toString();
    navigate(libraryPath(`lista/search?${advancedSearchQueryParameters}`));
    if (onSearch) {
      onSearch(searchValue, searchType);
    }
  };

  const onSubmit = (values) => {
    const searchValue = values?.value ? values?.value.trim() : '';
    if (!searchValue) {
      return;
    }
    
    if (onSubmitCustom) {
      onSubmitCustom(values);
      return;
    }
    
    executeSearch(values);
  };

  useEffect(() => {
    if (formikRef.current && !location.pathname.includes('/lista/search')) {
      formikRef.current.resetForm();
    }
  }, [location]);

  useEffect(() => {
    const prevPathname = prevPathnameRef.current;
    const currentPathname = location.pathname;
    
    if (showModal && 
        !prevPathname.includes('/lista/search') && 
        currentPathname.includes('/lista/search')) {
      setShowModal(false);
    }
    
    prevPathnameRef.current = currentPathname;
  }, [location.pathname, showModal]);

  const Input = ({ field, form, ...props }) => {
    return (
      <InputCustom
        icon={
          <button 
            type="button" 
            onClick={(e) => {
              e.preventDefault();
              setShowModal(true);
            }}
          >
            <AiOutlineSearch className="icon-input d-lg-none icon-input-navbar-mobile" />
          </button>
        }
        {...field}
        {...props}
        onChange={field.onChange}
      />
    );
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={formValues || { value: '', searchType: 'titulo' }}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {(formikProps) => {
        const handleDirectSubmit = (updatedSearchType = null) => {
          const valuesToUse = updatedSearchType 
            ? { ...formikProps.values, searchType: updatedSearchType }
            : formikProps.values;
          executeSearch(valuesToUse);
        };
        
        return (
          <Form role="search" className={isDesktop ? 'd-flex h-100' : ''}>
            <Field name="value" type="text" placeholder={t('buscarLibros')} component={Input} />
            <Field name="searchType" type="hidden" />
            {typeof children === 'function' ? children({ ...formikProps, handleDirectSubmit }) : children}
            {!isDesktop && (
              <SearchOptionsModal 
                show={showModal} 
                onHide={() => setShowModal(false)}
                searchValue={formikProps.values.value}
                currentSearchType={formikProps.values.searchType}
                onSelectOption={(value) => formikProps.setFieldValue('searchType', value)}
                onSubmit={(selectedSearchType) => handleDirectSubmit(selectedSearchType)}
                advancedSearchPath={libraryPath('lista/search')}
                onAdvancedSearchClick={() => {
                  formikProps.setFieldValue('value', '');
                }}
              />
            )}
          </Form>
        );
      }}
    </Formik>
  );
};

SearchBar.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  isDesktop: PropTypes.bool,
  initialValues: PropTypes.object,
  onSearch: PropTypes.func,
  onSubmitCustom: PropTypes.func,
};

export default SearchBar;
